import { RenderingContext } from './../../api';
import { anyToString, assertArray, assertObject, isNil, isNotNil } from './../../utils';
import { assertIsPlainObject, assertNotNil, isAssertOn } from './../internal/assert';
import { li } from './../services/logger/logger.service';
import { MarkupGenerator, MarkupProvider, MarkupProviders } from './../services/markup/markup.service';

/* Copyright IBM Corp. 2017 */
const LOGGER = 'MarkupUtils';

/*
 * Structure that identifies the (lazy) computation of piece of markup. This
 * object does NOT hold a reference to the rendering context
 */
export interface Markup {
    readonly componentId: string;
    readonly layoutMode: string;
    readonly templateRef: string;
    markup: string | undefined;
    template: MarkupGenerator | undefined;
}

export interface MarkupRef {
    readonly provider: MarkupProvider;
    readonly markupRef: Markup;
}

export interface MarkupMapping {
    readonly byTemplate: { [templateRef: string]: MarkupRef[] };
    readonly byId: { [id: string]: Markup[] };
}

function _defineAccessor(
    aRenderingContext: RenderingContext,
    aHandlebarsMarkup: Markup,
    aOptions?: any) {
    // log this
    li(LOGGER, 'defining markup accessor', aHandlebarsMarkup.layoutMode,
        aHandlebarsMarkup.componentId, aHandlebarsMarkup.templateRef);
    // make sure we have a markup element
    const markups = assertObject('markups', aRenderingContext);
    // define the property
    Object.defineProperty(markups, aHandlebarsMarkup.layoutMode, {
        enumerable: true,
        configurable: true,
        get: () => {
            // lazily compute the markup
            if (isNil(aHandlebarsMarkup.markup)) {
                // compute
                const template = aHandlebarsMarkup.template;
                if (isNil(template)) {
                    // undefined
                    aHandlebarsMarkup.markup = 'undefined';
                } else {
                    aHandlebarsMarkup.markup = template(aRenderingContext, aOptions);
                }
            }
            // ok
            return aHandlebarsMarkup.markup;
        }
    });
}

/*
 * Declares an accessor on the markup element of the handlebars context that executes the
 * template when the markup is first accessed.
 *
 * @param aContext  information about the handlebars context
 * @param aOptions  optional invocation options
 * @return the handlebars context
 */
function _defineAccessors(aRenderingContext: RenderingContext,
    aHandlebarsMarkup: Markup[],
    aOptions?: any) {
    // simply dispatch
    if (isNotNil(aHandlebarsMarkup)) {
        // iterate
        let lenMarkup = aHandlebarsMarkup.length;
        while (lenMarkup-- > 0) {
            _defineAccessor(aRenderingContext, aHandlebarsMarkup[lenMarkup], aOptions);
        }
    }
}

function _emptyMarkupMapping(): MarkupMapping {
    return {
        byId: {},
        byTemplate: {}
    };
}

function _addMarkup(aRenderingContext: RenderingContext, aMapping: MarkupMapping, aProviders: MarkupProviders) {
    // sanity check
    if (isAssertOn()) {
        assertNotNil(aRenderingContext);
        assertIsPlainObject(aRenderingContext);
    }
    // check for the layout
    const layouts = aRenderingContext.layouts;
    if (isNotNil(layouts)) {
        // sanity check
        if (isAssertOn()) {
            assertIsPlainObject(layouts);
        }
        // analyze the layouts
        // tslint:disable-next-line:forin
        for (const layoutMode in layouts) {
            // get the object
            const layout = layouts[layoutMode];
            // analyze the type
            const markupProvider = aProviders[layout.templateType];
            if (isNotNil(markupProvider)) {
                // we found the layout
                const id = anyToString(aRenderingContext.id);
                const templateRef = layout.template;
                const byTemplate: MarkupRef[] = assertArray<MarkupRef>(templateRef, aMapping.byTemplate);
                const byId: Markup[] = assertArray<Markup>(id, aMapping.byId);
                // markup context
                const markupRef: Markup = {
                    componentId: aRenderingContext.id,
                    layoutMode: layoutMode,
                    templateRef: templateRef,
                    markup: undefined,
                    template: undefined
                };
                // remember
                byTemplate.push({ provider: markupProvider, markupRef: markupRef });
                byId.push(markupRef);
            }
        }
    }
}

export {
    _defineAccessors as markupDefineAccessors,
    _addMarkup as markupAddMarkup,
    _emptyMarkupMapping as markupEmptyMarkupMapping
};

